// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Action,
    Translate,
} from '@plesk/ui-library';
import Status, { StatusType } from 'common/components/Status/Status';
import { ComputeResourceVmStatus } from 'common/api/resources/ComputeResourceVm';

export interface IVMStatusProps {
    status: string;
    isProcessing: boolean;
    progress?: number;
    iconOnly?: boolean;
}

export const statusMappings: Record<ComputeResourceVmStatus, string> = {
    [ComputeResourceVmStatus.CREATING]: 'servers.status.creating',
    [ComputeResourceVmStatus.RESIZING]: 'servers.status.resizing',
    [ComputeResourceVmStatus.RESTARTING]: 'servers.status.restarting',
    [ComputeResourceVmStatus.STOPPING]: 'servers.status.stopping',
    [ComputeResourceVmStatus.POWERING_OFF]: 'servers.status.poweringOff',
    [ComputeResourceVmStatus.STARTING]: 'servers.status.starting',
    [ComputeResourceVmStatus.SUSPENDING]: 'servers.status.suspending',
    [ComputeResourceVmStatus.RESUMING]: 'servers.status.resuming',
    [ComputeResourceVmStatus.REINSTALLING]: 'servers.status.reinstalling',
    [ComputeResourceVmStatus.MIGRATING]: 'servers.status.migrating',
    [ComputeResourceVmStatus.BACKUP_RESTORING]: 'servers.status.backupRestoring',
    [ComputeResourceVmStatus.BACKUP_CREATING]: 'servers.status.backupCreating',
    [ComputeResourceVmStatus.SNAPSHOT_CREATING]: 'servers.status.snapshotCreating',
    [ComputeResourceVmStatus.SNAPSHOT_REVERTING]: 'servers.status.snapshotReverting',
    [ComputeResourceVmStatus.SNAPSHOT_DELETING]: 'servers.status.snapshotDeleting',
    [ComputeResourceVmStatus.USAGE_RESETTING]: 'servers.status.usageResetting',
    [ComputeResourceVmStatus.CHANGING_PASSWORD]: 'servers.status.passwordChanging',
    [ComputeResourceVmStatus.UPDATING]: 'servers.status.updating',
    [ComputeResourceVmStatus.DELETING]: 'servers.status.deleting',
    [ComputeResourceVmStatus.UNAVAILABLE]: 'servers.status.unavailable',
    [ComputeResourceVmStatus.STARTED]: 'servers.status.running',
    [ComputeResourceVmStatus.STOPPED]: 'servers.status.stopped',
    [ComputeResourceVmStatus.PAUSED]: 'servers.status.paused',
    [ComputeResourceVmStatus.NOT_EXIST]: 'servers.status.notExist',
    [ComputeResourceVmStatus.DELETE_FAILED]: 'servers.status.deleteFailed',
    [ComputeResourceVmStatus.NO_STATE]: 'servers.status.noState',
    [ComputeResourceVmStatus.ADDITIONAL_IP_ADDING]: 'servers.status.additionalIpAdding',
    [ComputeResourceVmStatus.ADDITIONAL_IP_DELETING]: 'servers.status.additionalIpDeleting',
    [ComputeResourceVmStatus.PRIMARY_IP_CHANGING]: 'servers.status.primaryIpChanging',
    [ComputeResourceVmStatus.ADDITIONAL_DISK_ADDING]: 'servers.status.additionalDiskAdding',
    [ComputeResourceVmStatus.ADDITIONAL_DISK_DELETING]: 'servers.status.additionalDiskDeleting',
    [ComputeResourceVmStatus.ADDITIONAL_DISK_RESIZING]: 'servers.status.additionalDiskResizing',
    [ComputeResourceVmStatus.INSTALLING_GUEST_TOOLS]: 'servers.status.installingGuestTools',
    [ComputeResourceVmStatus.ATTACHING_TO_VPC_NETWORK]: 'servers.status.attachingToVpcNetwork',
    [ComputeResourceVmStatus.DETACHING_FROM_VPC_NETWORK]: 'servers.status.detachingFromVpcNetwork',
};

const roundedStatusMappings: Partial<Record<ComputeResourceVmStatus, StatusType>> = {
    [ComputeResourceVmStatus.STARTED]: StatusType.SUCCESS,
    [ComputeResourceVmStatus.STOPPED]: StatusType.STOPPED,
    [ComputeResourceVmStatus.PAUSED]: StatusType.PAUSED,
    [ComputeResourceVmStatus.UNAVAILABLE]: StatusType.UNAVAILABLE,
    [ComputeResourceVmStatus.NOT_EXIST]: StatusType.NOT_EXIST,
    // Unlike `not exist`, this one needs attention rather than just recording a fact, so it
    // gets the danger badge. This map is partial: without an entry the cell renders nothing.
    [ComputeResourceVmStatus.DELETE_FAILED]: StatusType.FAILED,
    [ComputeResourceVmStatus.NO_STATE]: StatusType.NO_STATE,
};

const statusWithProgress = [
    ComputeResourceVmStatus.CREATING,
    ComputeResourceVmStatus.REINSTALLING,
    ComputeResourceVmStatus.MIGRATING,
    ComputeResourceVmStatus.BACKUP_CREATING,
    ComputeResourceVmStatus.BACKUP_RESTORING,
];

export const VmStatus: React.FC<IVMStatusProps> = ({
    status,
    isProcessing,
    progress,
    iconOnly,
}) => {
    if (isProcessing) {
        const processingProgress = statusWithProgress.includes(status as ComputeResourceVmStatus) ? `${progress ?? 0}%` : '';
        return (
            <Action progress={true}>
                {!iconOnly && <><Translate content={statusMappings[status]} /> {processingProgress}</>}
            </Action>
        );
    }

    const roundedStatus = roundedStatusMappings[status as ComputeResourceVmStatus];
    if (!roundedStatus) {
        return null;
    }

    return (
        <Status
            value={roundedStatus}
            label={<Translate content={statusMappings[status]} />}
            iconOnly={iconOnly}
        />
    );
};
